import { GlobalOutlined, LogoutOutlined, UserOutlined } from '@ant-design/icons';
import { Avatar, Button, Dropdown, Flex, Grid, Layout, Select, Skeleton, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { Suspense, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth/AuthProvider';
import { useSiteContext } from '../context/SiteContext';
import type { AppLocale } from '../i18n';
import { brand, neutral } from '../theme';
import { ThemeToggle } from '../themeMode';
import { Logo } from './AppShell';
import { LANGUAGES, ProfileDrawer, useSaveLocale } from './ProfileDrawer';

/**
 * The frame of the screens outside a site: the vendor console and the first steps of a new
 * client. A white bar, as on Microsoft's sites: the brand, a divider, what the screen is about,
 * then the language and the account.
 */
export function SimpleShell({ context, children }: { context: ReactNode; children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const { signOut } = useAuth();
  const { me } = useSiteContext();
  const screens = Grid.useBreakpoint();
  const saveLocale = useSaveLocale();
  const [profileOpen, setProfileOpen] = useState(false);
  const displayName = me?.principal.displayName ?? me?.principal.email ?? '';
  const initials = displayName
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const menu: MenuProps = {
    items: [
      {
        key: 'who',
        disabled: true,
        label: (
          <div style={{ padding: '4px 0', maxWidth: 260 }}>
            <div style={{ fontWeight: 600, color: neutral.foreground1 }}>{displayName}</div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {me?.principal.email}
            </Typography.Text>
          </div>
        ),
      },
      { type: 'divider' },
      {
        key: 'profile',
        icon: <UserOutlined />,
        label: t('profile.title'),
        onClick: () => setProfileOpen(true),
      },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: t('app.signOut'),
        onClick: () => void signOut(),
      },
    ],
  };
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout.Header
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: screens.md ? '0 12px 0 4px' : '0 8px 0 0',
          borderBottom: `1px solid ${neutral.stroke2}`,
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <Logo collapsed={!screens.sm} bordered={false} />
        <div
          style={{
            flex: 1,
            minWidth: 0,
            color: neutral.foreground1,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            borderInlineStart: screens.sm ? `1px solid ${neutral.stroke1}` : undefined,
            paddingInlineStart: screens.sm ? 16 : 0,
            lineHeight: '24px',
          }}
        >
          {context}
        </div>
        <Select
          size="small"
          variant="borderless"
          value={i18n.language.slice(0, 2)}
          onChange={(v) => saveLocale(v as AppLocale)}
          prefix={<GlobalOutlined style={{ color: neutral.foreground3 }} />}
          options={LANGUAGES}
          labelRender={screens.md ? undefined : () => null}
          style={{ width: screens.md ? 132 : 44 }}
          aria-label={t('shell.language')}
        />
        <ThemeToggle />
        <Dropdown menu={menu} trigger={['click']} placement="bottomRight">
          <Button
            type="text"
            style={{ height: 40, paddingInline: 6, fontWeight: 400 }}
            aria-label={displayName}
          >
            <Flex align="center" gap={8}>
              <Avatar
                size={32}
                style={{ background: brand.background, fontWeight: 600, fontSize: 14 }}
              >
                {initials || '?'}
              </Avatar>
              {screens.lg && (
                <span style={{ color: neutral.foreground1, fontWeight: 600, fontSize: 14 }}>
                  {displayName}
                </span>
              )}
            </Flex>
          </Button>
        </Dropdown>
      </Layout.Header>
      <Layout.Content
        style={{
          padding: screens.md ? 24 : 16,
          maxWidth: 1280,
          width: '100%',
          margin: '0 auto',
        }}
      >
        <Suspense fallback={<Skeleton active paragraph={{ rows: 8 }} />}>{children}</Suspense>
      </Layout.Content>
      <ProfileDrawer open={profileOpen} onClose={() => setProfileOpen(false)} />
    </Layout>
  );
}
